<script setup lang="ts">
  /**
   * コンテンツのタイトル、講座名、タグ、説明を表示するコンポーネント
   * slot:under-tab タブの下に表示する
   * slot:append 説明エリアの下に表示する
   * slot:right-title タイトルエリアの右に表示する
   */
  import { Tag } from "~/entities/Tag";
  import { useLoginStore } from "~/stores/LoginStore";
  import { useHistoryStore } from "~/stores/HistoryStore";

  interface Props {
    tags?: Tag[];
    title?: string;
    description?: string;
    planId?: string;
    planTitle?: string;
    planIcon?: string;
    isFree?: boolean;
    loading?: boolean;
    contentId: string; // コンテンツID。今すぐ配信の場合は空
    isGood: boolean;
    goodCount: number;
    url: string | null;
    downloadable: boolean;
    isContract?: boolean;
    publishDateTime?: string;
    settingPath?: string;
    isBookmark?: boolean;
  }
  const props = defineProps<Props>();

  // このコンテンツの講座を見るボタン押下
  const planLink = computed<string>(() => `/plan/${props.planId}`);

  const loginStore = useLoginStore();
  const historyStore = useHistoryStore();

  // いいねボタンのクリック可能か
  const clickableGood = computed<boolean>(() => loginStore.isLogin() && !!props.contentId);

  const publishDateTimeText = computed<string>(() =>
    props.publishDateTime ? FormatDateString(props.publishDateTime, "yyyy/MM/dd(E)HH:mm") : " ",
  );

  const onClickSetting = () => {
    historyStore.to(props.settingPath);
  };
</script>

<template>
  <AtomsContentInformationCard>
    <template #header>
      <div class="d-flex">
        <div class="flex-grow-1">
          <p class="text-publish-date text-left text-monochrome-02">
            {{ publishDateTimeText }}
          </p>
        </div>

        <AtomsSettingButton v-if="settingPath" class="mr-2" @click.stop="onClickSetting" />

        <AtomsDownloadButton
          v-if="(isFree && downloadable) || (isContract && downloadable)"
          class="mr-2"
          :contents-id="contentId"
        />

        <AtomsCopyButton class="mr-2" :url="url" />

        <OrganismsContentsGoodButton
          v-if="goodCount !== undefined"
          class="mr-2"
          :content-id="contentId"
          :is-gooded="!!isGood"
          :count="goodCount ?? 0"
          :clickable="clickableGood"
        />
        <OrganismsContentsBookmarkButton :content-id="contentId" :is-bookmark="!!isBookmark" :clickable="true" />
      </div>
    </template>
    <!-- コンテンツタイトルエリア -->
    <div>
      <div v-if="!isFree" class="d-flex text-content-card-s text-truncate">
        <v-icon class="mr-0" icon="$mdiLock" />
        <span>会員限定</span>
      </div>
      <div class="d-flex title-text">
        {{ title }}
      </div>
    </div>
    <!-- 講座タイトル、タグエリア -->
    <div class="d-flex flex-wrap justify-space-between flex-grow-1">
      <div class="flex-grow-1">
        <v-sheet class="d-flex justify-space-between mx-0 my-2" color="transparent">
          <NuxtLink class="text-content-detail-s d-flex w-100 ga-2 text-white text-decoration-none" :to="planLink">
            <div>
              <AtomsImgWithLoading :src="planIcon" :width="80" :height="45" contains class="rounded" />
            </div>
            <div class="flex-grow-1">
              <p class="text-content-card-s">
                {{ planTitle }}
              </p>
            </div>
          </NuxtLink>
        </v-sheet>
        <v-sheet v-if="tags" class="mx-0 my-2 w-100 d-flex flex-wrap" color="transparent">
          <AtomsTagLabel v-for="(tag, i) in tags" :key="i" :tag="tag"></AtomsTagLabel>
        </v-sheet>
      </div>
      <v-sheet :min-width="240" color="transparent">
        <slot name="right-title"></slot>
      </v-sheet>
    </div>
    <v-sheet class="px-0 px-sm-4" color="transparent">
      <slot name="under-tab"></slot>
    </v-sheet>

    <MoleculesContentInformationDescription :description="description" />
  </AtomsContentInformationCard>
</template>

<style lang="scss" scoped>
  @import "assets/media.scss";

  .content-area {
    margin-top: 12px;
    margin-bottom: 20px;
    @include display_sp {
      margin-top: 12px;
    }
  }
  .title-text {
    font-size: 18px;
    font-weight: 700;
    line-height: 1.8em;
    letter-spacing: 0.06em;
  }
  .text-publish-date {
    font-size: 12px;
    font-weight: 400;
    line-height: 1.8em;
    letter-spacing: 0.06em;
  }
</style>
